// Persistent очередь недоставленных текстовых сообщений репетитора.
//
// Offline MVP: text-only → enqueue; при online drain зовёт
// `tutor.messages.enqueueSend` (сервер INSERT + Inngest AI). Ответ ассистента
// приходит через PowerSync. AI НЕ через PowerSync writeHandler.
//
// Хранение, межвкладочный lock, online-проба и фильтр владельца — общий движок
// `lib/idb-outbox.ts`. Здесь остаётся специфика сообщений: статусы для
// pending-пузыря, счётчик попыток, ручной retry и строгий FIFO (порядок реплик
// в диалоге — часть смысла, поэтому сбой одного сообщения останавливает волну).
//
// Счётчик → setPendingSyncCount('tutor-messages', n) для индикатора синка.

import { createIdbOutbox, type IdbOutbox } from "@/lib/idb-outbox";
import { setPendingSyncCount } from "@/lib/pending-sync-count";

/** Статус элемента outbox (для pending-пузыря в UI). */
export type TutorOutboxStatus = "queued" | "sending" | "failed";

export interface QueuedTutorMessage {
	clientMessageId: string;
	sessionId: string;
	text: string;
	/** Владелец очереди — не дренить чужие после смены аккаунта. */
	userId: string;
	enqueuedAt: number;
	status: TutorOutboxStatus;
	/** Когда статус стал sending (для TTL stale-sending). */
	sendingAt?: number;
	/** Последняя ошибка (для failed). */
	lastError?: string;
	attempts: number;
}

/** Сообщение выброшено безвозвратно — потеря обязана быть ВИДИМОЙ. */
export interface DroppedTutorMessage {
	clientMessageId: string;
	sessionId: string;
	text: string;
	reason: string;
}

export interface TutorMessageOutboxDeps {
	/** Текущий userId (фильтр drain + enqueue). */
	userId: string;
	/** Доставка одного элемента → tRPC enqueueSend. */
	send: (item: QueuedTutorMessage) => Promise<void>;
	/** Permanent-ошибка (коллизия id) → дроп из очереди. */
	isPermanentError: (err: unknown) => boolean;
	/**
	 * Сообщения выброшены навсегда. До 2026-08 их просто удаляли `del`-ом:
	 * написанный пользователем текст исчезал без следа, тогда как очередь чанков
	 * в том же положении показывала toast. Колбэк обязателен — молчаливая потеря
	 * пользовательского ввода недопустима.
	 */
	onDropped: (dropped: readonly DroppedTutorMessage[]) => void;
}

export interface TutorMessageOutbox {
	enqueue: (
		item: Omit<QueuedTutorMessage, "enqueuedAt" | "status" | "attempts" | "userId" | "sendingAt">,
	) => Promise<void>;
	/** Пометить failed → queued и сразу drain. */
	retry: (clientMessageId: string) => Promise<void>;
	drain: () => Promise<void>;
	list: () => Promise<QueuedTutorMessage[]>;
	/** Сообщения конкретной сессии (для optimistic bubbles). */
	listForSession: (sessionId: string) => Promise<QueuedTutorMessage[]>;
	/** Подписка на изменения очереди (UI pending-статусы). */
	subscribe: (listener: () => void) => () => void;
	getStatus: (clientMessageId: string) => TutorOutboxStatus | null;
	refreshCount: () => Promise<void>;
	startBackground: () => () => void;
}

/** После kill mid-send вернуть в queued (иначе вечный skip). */
const SENDING_STALE_MS = 60_000;

/** Singleton outbox (один на вкладку; drain сериализуется locks). */
let singleton: TutorMessageOutbox | null = null;
let singletonUserId: string | null = null;

export function getTutorMessageOutbox(deps?: TutorMessageOutboxDeps): TutorMessageOutbox {
	if (singleton && deps && singletonUserId === deps.userId) return singleton;
	if (singleton && deps && singletonUserId !== deps.userId) {
		// Смена аккаунта — новый singleton (старые items остаются в IDB, но
		// фильтруются по userId при list/drain).
		singleton = null;
		singletonUserId = null;
	}
	if (singleton) return singleton;
	if (!deps) {
		throw new Error("tutor-message-outbox: first call requires deps");
	}
	singleton = createTutorMessageOutbox(deps);
	singletonUserId = deps.userId;
	return singleton;
}

/** Для тестов — сброс singleton. */
export function resetTutorMessageOutboxForTests(): void {
	singleton = null;
	singletonUserId = null;
}

export function createTutorMessageOutbox(deps: TutorMessageOutboxDeps): TutorMessageOutbox {
	const listeners = new Set<() => void>();
	/** In-memory cache статусов для синхронного getStatus в render. */
	const statusCache = new Map<string, TutorOutboxStatus>();

	const emit = (): void => {
		for (const l of listeners) l();
	};

	const outbox: IdbOutbox<QueuedTutorMessage> = createIdbOutbox<QueuedTutorMessage>({
		dbName: "studyhub-tutor-message-outbox",
		storeName: "pending-messages",
		keyPrefix: "msg:",
		lockName: "studyhub-tutor-message-outbox-drain",
		keyOf: (m) => m.clientMessageId,
		ownerOf: (m) => m.userId,
		userId: deps.userId,
		compare: (a, b) => a.enqueuedAt - b.enqueuedAt,
		// Stale sending после crash → снова queued (иначе элемент вечно skip'ается).
		hydrate: (item) => {
			if (item.status !== "sending") return item;
			const stale = item.sendingAt == null || Date.now() - item.sendingAt > SENDING_STALE_MS;
			return stale ? { ...item, status: "queued", sendingAt: undefined } : item;
		},
		// Отправкой этого элемента занята другая волна.
		skip: (item) => item.status === "sending",
		send: deps.send,
		isPermanentError: deps.isPermanentError,
		// Порядок реплик диалога — часть смысла: не отправлять следующие, пока
		// текущее не прошло.
		stopOnTransientError: true,
		beforeSend: async (item) => {
			await patchStatus(item.clientMessageId, { status: "sending", sendingAt: Date.now() });
		},
		onTransientError: async (item, err) => {
			await patchStatus(item.clientMessageId, {
				status: "failed",
				sendingAt: undefined,
				attempts: item.attempts + 1,
				lastError: err instanceof Error ? err.message : String(err),
			});
		},
		onCountChange: (count) => {
			setPendingSyncCount("tutor-messages", count);
			emit();
		},
		onDrained: ({ sent, dropped }) => {
			for (const item of sent) statusCache.delete(item.clientMessageId);
			if (dropped.length === 0) return;
			for (const { item } of dropped) statusCache.delete(item.clientMessageId);
			deps.onDropped(
				dropped.map(({ item, err }) => ({
					clientMessageId: item.clientMessageId,
					sessionId: item.sessionId,
					text: item.text,
					reason: err instanceof Error ? err.message : String(err),
				})),
			);
			emit();
		},
	});

	async function patchStatus(
		clientMessageId: string,
		patch: Partial<QueuedTutorMessage>,
	): Promise<void> {
		const next = await outbox.patch(clientMessageId, patch);
		if (!next) return;
		statusCache.set(clientMessageId, next.status);
		emit();
	}

	const listAll = async (): Promise<QueuedTutorMessage[]> => {
		const items = await outbox.list();
		statusCache.clear();
		for (const item of items) statusCache.set(item.clientMessageId, item.status);
		return items;
	};

	return {
		enqueue: async (item) => {
			const queued: QueuedTutorMessage = {
				...item,
				userId: deps.userId,
				enqueuedAt: Date.now(),
				status: "queued",
				attempts: 0,
			};
			await outbox.put(queued);
			statusCache.set(item.clientMessageId, "queued");
			emit();
		},
		retry: async (clientMessageId) => {
			await patchStatus(clientMessageId, {
				status: "queued",
				lastError: undefined,
				sendingAt: undefined,
			});
			await outbox.refreshCount();
			void outbox.drain();
		},
		drain: outbox.drain,
		list: listAll,
		listForSession: async (sessionId) => {
			const all = await listAll();
			return all.filter((i) => i.sessionId === sessionId);
		},
		subscribe: (listener) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		getStatus: (clientMessageId) => statusCache.get(clientMessageId) ?? null,
		refreshCount: outbox.refreshCount,
		startBackground: outbox.startBackground,
	};
}
